import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { SyncStatusItem } from '@oxxean/types';
import { fetchSyncStatus } from '../lib/api/sync';
import { queryKeys } from '../lib/query-client';
import { formatDateTime, formatDuration, formatNumber } from '../lib/format';
import { SYNC_STATUS_META, worstSyncStatus } from '../lib/sync-display';
import { Icon } from './Icon';
import { Button } from './primitives';
import { InlineError } from './states';

/** Píldora de estado con su etiqueta e icono. */
export function SyncBadge({
  item,
}: {
  item: Pick<SyncStatusItem, 'status' | 'sourceLabel'>;
}): JSX.Element {
  const meta = SYNC_STATUS_META[item.status];
  return (
    <span className="syncdot" data-status={item.status} title={meta.description}>
      <Icon name={meta.icon} size={13} />
      {meta.label}
    </span>
  );
}

/** Detalle de un origen: contadores, período y error real sin ocultar. */
export function SyncSourceDetail({ item }: { item: SyncStatusItem }): JSX.Element {
  const meta = SYNC_STATUS_META[item.status];
  return (
    <article className="sync-item">
      <div className="sync-item__top">
        <span className="sync-item__name">{item.sourceLabel}</span>
        <SyncBadge item={item} />
      </div>
      <p className="tiny muted" style={{ marginTop: 4 }}>
        {meta.description}
      </p>
      <div className="sync-item__meta">
        <span>Última ejecución: {formatDateTime(item.lastRunAt)}</span>
        <span>Último éxito: {formatDateTime(item.lastSuccessAt)}</span>
        <span>
          Período: {item.periodFrom === null ? '—' : item.periodFrom} a{' '}
          {item.periodTo === null ? '—' : item.periodTo}
        </span>
        <span>Leídos: {formatNumber(item.recordsRead)}</span>
        <span>Creados: {formatNumber(item.recordsCreated)}</span>
        <span>Actualizados: {formatNumber(item.recordsUpdated)}</span>
        <span>Omitidos: {formatNumber(item.recordsSkipped)}</span>
        <span>Con error: {formatNumber(item.recordsFailed)}</span>
        <span>Duración: {formatDuration(item.durationMs)}</span>
      </div>
      {item.errorCount > 0 && (
        <p className="sync-item__err">
          {formatNumber(item.errorCount)} error(es).{' '}
          {item.lastErrorCode !== null && <strong>[{item.lastErrorCode}] </strong>}
          {item.lastErrorMessage ?? 'Sin mensaje asociado.'}
        </p>
      )}
    </article>
  );
}

/**
 * Indicador del encabezado.
 *
 * Muestra el estado más grave de todos los orígenes y despliega el detalle
 * completo. Se refresca solo cada minuto porque una sincronización en curso debe
 * verse avanzar sin recargar la página.
 */
export function SyncStatusIndicator(): JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const statusQuery = useQuery({
    queryKey: queryKeys.syncStatus,
    queryFn: fetchSyncStatus,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items = statusQuery.data ?? [];
  const worst = worstSyncStatus(items.map((item) => item.status));

  const label =
    statusQuery.isLoading
      ? 'Consultando…'
      : statusQuery.isError
        ? 'Estado desconocido'
        : worst === null
          ? SYNC_STATUS_META.NO_DATA.label
          : SYNC_STATUS_META[worst].label;

  return (
    <div className="popover" ref={containerRef}>
      <button
        type="button"
        className="syncdot"
        data-status={worst ?? 'NO_DATA'}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title="Estado de sincronización con BUK"
      >
        <Icon name={worst === null ? 'database-off' : SYNC_STATUS_META[worst].icon} size={13} />
        <span className="nowrap">Sincronización: {label}</span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={12} />
      </button>

      {open && (
        <div className="popover__panel" role="dialog" aria-label="Estado de sincronización" style={{ width: 360 }}>
          <div className="popover__head">
            <strong style={{ fontSize: 12.5 }}>Estado de sincronización</strong>
            <p className="tiny muted">Origen de todos los indicadores de la plataforma.</p>
          </div>

          {statusQuery.isError ? (
            <InlineError error={statusQuery.error} onRetry={() => void statusQuery.refetch()} />
          ) : statusQuery.isLoading ? (
            <p className="tiny muted" style={{ padding: 8 }}>
              Consultando el estado de los orígenes…
            </p>
          ) : items.length === 0 ? (
            <p className="tiny muted" style={{ padding: 8 }}>
              No hay orígenes configurados todavía.
            </p>
          ) : (
            <div className="sync-list">
              {items.map((item) => (
                <SyncSourceDetail key={item.source} item={item} />
              ))}
            </div>
          )}

          <div className="popover__sep" />
          <Link className="popover__item" to="/sincronizaciones" onClick={() => setOpen(false)}>
            <Icon name="sync" size={14} />
            Ver historial de ejecuciones
          </Link>
          <Button
            variant="ghost"
            size="sm"
            icon="refresh"
            onClick={() => void statusQuery.refetch()}
            block
          >
            Volver a consultar
          </Button>
        </div>
      )}
    </div>
  );
}
